import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Lock, Visibility, VisibilityOff } from "@mui/icons-material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import logo from "../assets/logo-T-color.jpg";
import routes from "../router/routes";
import { useApiPasswordResetConfirm } from "../hooks/api/useApiPasswordResetConfirm";

const MIN_PASSWORD_LENGTH = 8;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const navigate = useNavigate();

  const { submit, isLoading } = useApiPasswordResetConfirm();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDone, setIsDone] = useState(false);

  const handleSubmit = async () => {
    setErrorMessage("");

    if (!token) {
      setErrorMessage(
        "リンクが正しくありません。メールのリンクをもう一度お試しください。"
      );
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setErrorMessage(
        `パスワードは${MIN_PASSWORD_LENGTH}文字以上で入力してください。`
      );
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage("パスワードが一致しません。");
      return;
    }

    const ok = await submit(token, password);
    if (ok) {
      setIsDone(true);
    } else {
      setErrorMessage(
        "パスワードの再設定に失敗しました。リンクの有効期限が切れているか、既に使用済みの可能性があります。お手数ですが再度お手続きください。"
      );
    }
  };

  return (
    <Box
      sx={{
        maxWidth: "400px",
        margin: "50px auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
        <img
          src={logo}
          alt="ラクロジロゴ"
          style={{ width: "150px", height: "auto" }}
        />
      </Box>

      <Typography variant="h6" sx={{ fontSize: "16px", fontWeight: "bold", mb: 2 }}>
        パスワード再設定
      </Typography>

      {isDone ? (
        <>
          <Typography sx={{ fontSize: "14px", textAlign: "center", mb: 3 }}>
            パスワードを再設定しました。
            <br />
            新しいパスワードでログインしてください。
          </Typography>
          <Button
            variant="contained"
            fullWidth
            sx={{ backgroundColor: "#4a90e2", "&:hover": { backgroundColor: "#357ac9" } }}
            onClick={() => navigate(routes.login)}
          >
            ログイン画面へ
          </Button>
        </>
      ) : (
        <>
          {errorMessage && (
            <p style={{ color: "red", fontSize: "13px", textAlign: "center" }}>
              {errorMessage}
            </p>
          )}

          <Typography
            variant="h6"
            sx={{ alignSelf: "flex-start", marginTop: "15px", fontSize: "14px", fontWeight: "bold" }}
          >
            新しいパスワード
          </Typography>
          <TextField
            placeholder="New Password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            sx={{ marginTop: "5px", marginBottom: "15px" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Typography
            variant="h6"
            sx={{ alignSelf: "flex-start", marginTop: "15px", fontSize: "14px", fontWeight: "bold" }}
          >
            新しいパスワード（確認用）
          </Typography>
          <TextField
            placeholder="Confirm Password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            sx={{ marginTop: "5px", marginBottom: "15px" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock />
                </InputAdornment>
              ),
            }}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSubmit();
            }}
          />

          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={isLoading}
            sx={{
              backgroundColor: "#4a90e2",
              color: "white",
              marginTop: "20px",
              "&:hover": { backgroundColor: "#357ac9" },
            }}
          >
            {isLoading ? "設定中..." : "パスワードを再設定する"}
          </Button>

          <Typography
            component={Link}
            to={routes.login}
            sx={{
              marginTop: "15px",
              fontSize: "12px",
              color: "#555",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            ログイン画面へ戻る
          </Typography>
        </>
      )}
    </Box>
  );
};

export default ResetPassword;
